/**
 * Feature Flag Context Module
 *
 * Provides React context for feature flag management
 *
 * Provides the following features:
 * - Feature flag state management
 * - Flag toggle functionality
 * - Context provider component
 * - Custom hook for easy access
 *
 * @module FeatureFlagContext
 */

import { createContext, useContext, type ReactNode } from 'react';
import { Observer } from 'mobx-react-lite';
import { featureFlagStore, IFeatureFlagStore } from '../stores';

/**
 * Feature Flag Context Interface
 *
 * @interface FeatureFlagContextType
 * @property {function} isEnabled - Check if a feature flag is enabled
 * @property {function} enable - Enable a feature flag
 * @property {function} disable - Disable a feature flag
 */
type FeatureFlagContextType = Pick<IFeatureFlagStore, 'isEnabled' | 'enable' | 'disable'>;

/**
 * Props for FeatureFlagProvider component
 */
interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Feature Flag Context
 * Provides feature flag state and operations
 */
const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

/**
 * Feature Flag Provider Component
 * Manages feature flag state and initialization
 *
 * @param {FeatureFlagProviderProps} props - Component props
 * @returns {JSX.Element} Provider component
 *
 * @example
 * <FeatureFlagProvider>
 *   <App />
 * </FeatureFlagProvider>
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  return (
    <Observer>
      {() => {
        if (featureFlagStore.isLoading) {
          return <div>Loading...</div>;
        }

        if (featureFlagStore.error) {
          return <div>Error: {featureFlagStore.error.message}</div>;
        }

        return (
          <FeatureFlagContext.Provider
            value={{
              isEnabled: (flag) => featureFlagStore.isEnabled(flag),
              enable: (flag) => featureFlagStore.enable(flag),
              disable: (flag) => featureFlagStore.disable(flag),
            }}>
            {children}
          </FeatureFlagContext.Provider>
        );
      }}
    </Observer>
  );
}

/**
 * Custom hook for accessing feature flags
 *
 * @hook useFeatureFlag
 * @returns {FeatureFlagContextType} Feature flag context methods
 * @throws {Error} When used outside FeatureFlagProvider
 *
 * @example
 * const { isEnabled } = useFeatureFlag();
 * if (isEnabled(FeatureFlag.SMART_EDITOR)) {
 *   // Feature is enabled
 * }
 */
export const useFeatureFlag = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlag must be used within FeatureFlagProvider');
  }
  return context;
};
